import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { COURSE_API_URL } from './lib/api-config';
import { useAuth } from './contexts/AuthContext';
import { coursesApi, instancesApi, venuesApi, getErrorMessage } from './services/api';
import { CourseTemplateTab, CourseModal, DEFAULT_NOTIFICATION_SETTINGS } from './components/courses/CourseTemplateTab';
import { VenueTab, VenueModal } from './components/courses/VenueTab';
import { InstanceTab, InstanceModal } from './components/courses/InstanceTab';
import { CompanyTab, CompanyModal } from './components/courses/CompanyTab';
import BookingModal from './components/courses/BookingModal';
import SurveyResultsModal from './components/courses/SurveyResultsModal';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';
import './Courses.css';

const EMPTY_VENUE_FORM = {
  name: '', address: '', city: '', contactPerson: '', contactEmail: '',
  contactPhone: '', pricePerDay: '', cancellationDays: '14', cancellationPolicy: '',
  capacity: '', facilities: '', rating: '', notes: '', isActive: true
};

const EMPTY_COURSE_FORM = {
  name: '', description: '', price: '', durationDays: '1', maxParticipants: '20',
  minParticipants: '5', prerequisites: '', learningObjectives: '', infoPageUrl: '',
  surveyFormUrl: '', isActive: true, notificationSettings: { ...DEFAULT_NOTIFICATION_SETTINGS }
};

const EMPTY_INSTANCE_FORM = {
  courseId: '', venueId: '', location: '', city: '', address: '',
  startDate: '', endDate: '', startTime: '08:30', endTime: '16:00',
  instructorName: '', instructorEmail: '', maxParticipants: '12', price: '',
  earlyBirdPrice: '', earlyBirdDeadline: '', status: 'Planned',
  isPublished: false, isInternal: false, companyId: '', corporateCompany: '', corporateTotalPrice: '',
  teamsLink: '', courseMaterialLink: '', evaluationLink: '', notes: '',
  venueCost: '', venuePaidBy: '', coordinatorEmail: '',
  notificationSettings: {
    bookingConfirmation: null, bookingSmsConfirmation: null, completionReminder: null,
    preCourseInfo: null, attendanceLink: null, diplomaSurvey: null, surveyReminder: null,
    teamsMeetingReminder: null
  }
};

const EMPTY_BOOKING_DATA = {
  participantFirstName: '', participantLastName: '', participantEmail: '',
  participantPhone: '', company: '', invoiceReference: '', specialDiet: ''
};

const EMPTY_COMPANY_FORM = {
  name: '', organizationNumber: '', billingAddress: '', billingEmail: '',
  contactPerson: '', contactEmail: '', contactPhone: '',
  defaultInvoiceReference: '', defaultCostCenter: '', portalSlug: '',
  notes: '', isActive: true
};

function Courses() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['admin', 'teacher']);

  const [instances, setInstances] = useState([]);
  const [courses, setCourses] = useState([]);
  const [venues, setVenues] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('instances');

  // Modal state
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [venueForm, setVenueForm] = useState({ ...EMPTY_VENUE_FORM });

  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({ ...EMPTY_COURSE_FORM });

  const [showInstanceModal, setShowInstanceModal] = useState(false);
  const [editingInstance, setEditingInstance] = useState(null);
  const [instanceForm, setInstanceForm] = useState({ ...EMPTY_INSTANCE_FORM });

  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [bookingData, setBookingData] = useState({ ...EMPTY_BOOKING_DATA });

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ ...EMPTY_COMPANY_FORM });

  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyInstance, setSurveyInstance] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [instancesRes, coursesRes, venuesRes, companiesRes] = await Promise.all([
        isAdmin ? instancesApi.getAll() : instancesApi.getUpcoming(),
        isAdmin ? coursesApi.getAll() : Promise.resolve({ data: { data: [] } }),
        isAdmin ? venuesApi.getAll() : Promise.resolve({ data: { data: [] } }),
        isAdmin ? axios.get(`${COURSE_API_URL}/companies`, { withCredentials: true }) : Promise.resolve({ data: { data: [] } })
      ]);
      setInstances(instancesRes.data.data);
      setCourses(coursesRes.data.data || []);
      setVenues(venuesRes.data.data || []);
      setCompanies(companiesRes.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Fel vid hämtning:', error);
      setError(getErrorMessage(error));
      setLoading(false);
    }
  };

  // === Course CRUD ===
  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = { ...courseForm, notificationSettings: courseForm.notificationSettings };
    try {
      if (editingCourse) {
        await coursesApi.update(editingCourse.id, payload);
        setSuccess('Kurs uppdaterad');
      } else {
        await coursesApi.create(payload);
        setSuccess('Kurs skapad');
      }
      setShowCourseModal(false);
      setCourseForm({ ...EMPTY_COURSE_FORM });
      setEditingCourse(null);
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleEditCourse = (course) => {
    setCourseForm({
      name: course.name || '', description: course.description || '',
      price: course.price || '', durationDays: course.durationDays?.toString() || '1',
      maxParticipants: course.maxParticipants?.toString() || '20',
      minParticipants: course.minParticipants?.toString() || '5',
      prerequisites: course.prerequisites || '', learningObjectives: course.learningObjectives || '',
      infoPageUrl: course.infoPageUrl || '', surveyFormUrl: course.surveyFormUrl || '',
      isActive: course.isActive !== false,
      notificationSettings: course.notificationSettings || { ...DEFAULT_NOTIFICATION_SETTINGS }
    });
    setEditingCourse(course);
    setShowCourseModal(true);
  };

  const handleDeleteCourse = async (course) => {
    if (!window.confirm(`Ta bort kursen "${course.name}"? Detta går inte att ångra.`)) return;
    try {
      await coursesApi.delete(course.id);
      setSuccess('Kurs borttagen');
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  // === Venue CRUD ===
  const handleVenueSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      name: venueForm.name, address: venueForm.address, city: venueForm.city,
      contactPerson: venueForm.contactPerson || null, contactEmail: venueForm.contactEmail || null,
      contactPhone: venueForm.contactPhone || null,
      pricePerDay: venueForm.pricePerDay ? parseFloat(venueForm.pricePerDay) : null,
      cancellationDays: parseInt(venueForm.cancellationDays) || 14,
      cancellationPolicy: venueForm.cancellationPolicy || null,
      capacity: venueForm.capacity ? parseInt(venueForm.capacity) : null,
      facilities: venueForm.facilities || null,
      rating: venueForm.rating ? parseInt(venueForm.rating) : null,
      notes: venueForm.notes || null, isActive: venueForm.isActive
    };
    try {
      if (editingVenue) {
        await venuesApi.update(editingVenue.id, payload);
        setSuccess('Lokal uppdaterad');
      } else {
        await venuesApi.create(payload);
        setSuccess('Lokal skapad');
      }
      setShowVenueModal(false);
      setVenueForm({ ...EMPTY_VENUE_FORM });
      setEditingVenue(null);
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleEditVenue = (venue) => {
    setVenueForm({
      name: venue.name || '', address: venue.address || '', city: venue.city || '',
      contactPerson: venue.contactPerson || '', contactEmail: venue.contactEmail || '',
      contactPhone: venue.contactPhone || '',
      pricePerDay: venue.pricePerDay?.toString() || '',
      cancellationDays: venue.cancellationDays?.toString() || '14',
      cancellationPolicy: venue.cancellationPolicy || '',
      capacity: venue.capacity?.toString() || '', facilities: venue.facilities || '',
      rating: venue.rating?.toString() || '', notes: venue.notes || '',
      isActive: venue.isActive !== false
    });
    setEditingVenue(venue);
    setShowVenueModal(true);
  };

  const handleDeleteVenue = async (venue) => {
    if (!window.confirm(`Ta bort lokalen "${venue.name}"? Detta går inte att ångra.`)) return;
    try {
      await venuesApi.delete(venue.id);
      setSuccess('Lokal borttagen');
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  // === Instance CRUD ===
  const handleInstanceSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!instanceForm.courseId) {
      setError('Du måste välja en kursmall');
      return;
    }
    if (!instanceForm.startDate) {
      setError('Du måste ange startdatum');
      return;
    }
    const payload = {
      courseId: parseInt(instanceForm.courseId),
      venueId: instanceForm.venueId ? parseInt(instanceForm.venueId) : null,
      location: instanceForm.location || null, city: instanceForm.city || null,
      address: instanceForm.address || null, startDate: instanceForm.startDate,
      endDate: instanceForm.endDate || instanceForm.startDate,
      startTime: instanceForm.startTime, endTime: instanceForm.endTime,
      instructorName: instanceForm.instructorName,
      instructorEmail: instanceForm.instructorEmail || null,
      maxParticipants: parseInt(instanceForm.maxParticipants),
      price: instanceForm.price ? parseFloat(instanceForm.price) : null,
      earlyBirdPrice: instanceForm.earlyBirdPrice ? parseFloat(instanceForm.earlyBirdPrice) : null,
      earlyBirdDeadline: instanceForm.earlyBirdDeadline || null,
      status: instanceForm.status, isPublished: instanceForm.isPublished,
      isInternal: instanceForm.isInternal,
      companyId: instanceForm.companyId ? parseInt(instanceForm.companyId) : null,
      corporateCompany: instanceForm.corporateCompany || null,
      corporateTotalPrice: instanceForm.corporateTotalPrice ? parseFloat(instanceForm.corporateTotalPrice) : null,
      teamsLink: instanceForm.teamsLink || null, courseMaterialLink: instanceForm.courseMaterialLink || null,
      evaluationLink: instanceForm.evaluationLink || null, notes: instanceForm.notes || null,
      venueCost: instanceForm.venueCost ? parseFloat(instanceForm.venueCost) : null,
      venuePaidBy: instanceForm.venuePaidBy || null,
      coordinatorEmail: instanceForm.coordinatorEmail || null,
      notificationSettings: instanceForm.notificationSettings
    };
    try {
      if (editingInstance) {
        await instancesApi.update(editingInstance.id, payload);
        setSuccess('Kurstillfälle uppdaterat');
      } else {
        await instancesApi.create(payload);
        setSuccess('Kurstillfälle skapat');
      }
      setShowInstanceModal(false);
      setInstanceForm({ ...EMPTY_INSTANCE_FORM });
      setEditingInstance(null);
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleEditInstance = (instance) => {
    setInstanceForm({
      courseId: instance.courseId?.toString() || instance.course?.id?.toString() || '',
      venueId: instance.venueId?.toString() || '', location: instance.location || '',
      city: instance.city || '', address: instance.address || '',
      startDate: instance.startDate?.split('T')[0] || '',
      endDate: instance.endDate?.split('T')[0] || '',
      startTime: instance.startTime || '09:00', endTime: instance.endTime || '16:00',
      instructorName: instance.instructorName || '', instructorEmail: instance.instructorEmail || '',
      maxParticipants: instance.maxParticipants?.toString() || '20',
      price: instance.price?.toString() || '', earlyBirdPrice: instance.earlyBirdPrice?.toString() || '',
      earlyBirdDeadline: instance.earlyBirdDeadline?.split('T')[0] || '',
      status: instance.status || 'Planned', isPublished: instance.isPublished || false,
      isInternal: instance.isInternal || false, companyId: instance.companyId?.toString() || '',
      corporateCompany: instance.corporateCompany || '',
      corporateTotalPrice: instance.corporateTotalPrice || '', teamsLink: instance.teamsLink || '',
      courseMaterialLink: instance.courseMaterialLink || '', evaluationLink: instance.evaluationLink || '',
      notes: instance.notes || '', venueCost: instance.venueCost?.toString() || '',
      venuePaidBy: instance.venuePaidBy || '', coordinatorEmail: instance.coordinatorEmail || '',
      notificationSettings: instance.notificationSettings || { ...EMPTY_INSTANCE_FORM.notificationSettings }
    });
    setEditingInstance(instance);
    setShowInstanceModal(true);
  };

  const handleDeleteInstance = async (instance) => {
    const dateStr = new Date(instance.startDate).toLocaleDateString('sv-SE');
    if (!window.confirm(`Ta bort kurstillfället ${dateStr}?`)) return;
    try {
      await instancesApi.delete(instance.id);
      setSuccess('Kurstillfälle borttaget');
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  // === Booking handlers ===
  const handleBookingClick = (instance) => {
    setSelectedInstance(instance);
    setShowBookingForm(true);
    setBookingSuccess(null);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${COURSE_API_URL}/bookings`, {
        courseInstanceId: selectedInstance.id,
        ...bookingData
      }, { withCredentials: true });
      setBookingSuccess(response.data.message);
      setBookingData({ ...EMPTY_BOOKING_DATA });
      fetchData();
      setTimeout(() => {
        setShowBookingForm(false);
        setBookingSuccess(null);
      }, 3000);
    } catch (error) {
      setError('Fel vid bokning: ' + (error.response?.data?.error || 'Något gick fel'));
      setTimeout(() => setError(''), 5000);
    }
  };

  // === Company CRUD ===
  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setError('');
    const config = { withCredentials: true };
    const payload = {
      name: companyForm.name,
      organizationNumber: companyForm.organizationNumber || null,
      billingAddress: companyForm.billingAddress || null,
      billingEmail: companyForm.billingEmail || null,
      contactPerson: companyForm.contactPerson || null,
      contactEmail: companyForm.contactEmail || null,
      contactPhone: companyForm.contactPhone || null,
      defaultInvoiceReference: companyForm.defaultInvoiceReference || null,
      defaultCostCenter: companyForm.defaultCostCenter || null,
      portalSlug: companyForm.portalSlug || null,
      notes: companyForm.notes || null,
      isActive: companyForm.isActive
    };
    try {
      if (editingCompany) {
        await axios.put(`${COURSE_API_URL}/companies/${editingCompany.id}`, payload, config);
        setSuccess('Företag uppdaterat');
      } else {
        await axios.post(`${COURSE_API_URL}/companies`, payload, config);
        setSuccess('Företag skapat');
      }
      setShowCompanyModal(false);
      setCompanyForm({ ...EMPTY_COMPANY_FORM });
      setEditingCompany(null);
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  const handleEditCompany = (company) => {
    setCompanyForm({
      name: company.name || '', organizationNumber: company.organizationNumber || '',
      billingAddress: company.billingAddress || '', billingEmail: company.billingEmail || '',
      contactPerson: company.contactPerson || '', contactEmail: company.contactEmail || '',
      contactPhone: company.contactPhone || '',
      defaultInvoiceReference: company.defaultInvoiceReference || '',
      defaultCostCenter: company.defaultCostCenter || '',
      portalSlug: company.portalSlug || '', notes: company.notes || '',
      isActive: company.isActive !== false
    });
    setEditingCompany(company);
    setShowCompanyModal(true);
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Ta bort företaget "${company.name}"? Detta går inte att ångra.`)) return;
    const config = { withCredentials: true };
    try {
      await axios.delete(`${COURSE_API_URL}/companies/${company.id}`, config);
      setSuccess('Företag borttaget');
      fetchData();
    } catch (error) {
      setError(getErrorMessage(error));
    }
  };

  // === Helpers ===
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatPrice = (price, discount) => {
    if (discount > 0) {
      const discountedPrice = price * (1 - discount / 100);
      return (
        <>
          <span className="original-price">{price} kr</span>
          <span className="discounted-price">{discountedPrice} kr</span>
          <span className="discount-badge">-{discount}%</span>
        </>
      );
    }
    return `${price} kr`;
  };

  if (loading) {
    return <LoadingSpinner message="Laddar kurser..." />;
  }

  return (
    <div className="courses-container">
      {isAdmin && (
        <div className="admin-tabs">
          <button className={`tab ${activeTab === 'instances' ? 'active' : ''}`} onClick={() => setActiveTab('instances')}>
            Kurstillfällen
          </button>
          <button className={`tab ${activeTab === 'courses' ? 'active' : ''}`} onClick={() => setActiveTab('courses')}>
            Kursmallar
          </button>
          <button className={`tab ${activeTab === 'venues' ? 'active' : ''}`} onClick={() => setActiveTab('venues')}>
            Lokaler
          </button>
          <button className={`tab ${activeTab === 'companies' ? 'active' : ''}`} onClick={() => setActiveTab('companies')}>
            Företag
          </button>
        </div>
      )}

      <AlertMessage error={error} success={success} />

      {isAdmin && activeTab === 'courses' && (
        <CourseTemplateTab
          courses={courses}
          onEdit={handleEditCourse}
          onDelete={handleDeleteCourse}
          onNew={() => { setCourseForm({ ...EMPTY_COURSE_FORM }); setEditingCourse(null); setShowCourseModal(true); }}
        />
      )}

      {isAdmin && activeTab === 'venues' && (
        <VenueTab
          venues={venues}
          onEdit={handleEditVenue}
          onDelete={handleDeleteVenue}
          onNew={() => { setVenueForm({ ...EMPTY_VENUE_FORM }); setEditingVenue(null); setShowVenueModal(true); }}
        />
      )}

      {isAdmin && activeTab === 'companies' && (
        <CompanyTab
          companies={companies}
          onEdit={handleEditCompany}
          onDelete={handleDeleteCompany}
          onNew={() => { setCompanyForm({ ...EMPTY_COMPANY_FORM }); setEditingCompany(null); setShowCompanyModal(true); }}
        />
      )}

      {(activeTab === 'instances' || !isAdmin) && (
        <InstanceTab
          instances={instances}
          isAdmin={isAdmin}
          onEdit={handleEditInstance}
          onDelete={handleDeleteInstance}
          onNew={() => { setInstanceForm({ ...EMPTY_INSTANCE_FORM }); setEditingInstance(null); setShowInstanceModal(true); }}
          onBook={handleBookingClick}
          onViewSurvey={(instance) => { setSurveyInstance(instance); setShowSurveyModal(true); }}
          onLockAttendance={async (instance) => {
            if (!window.confirm(`Markera alla deltagare som närvarande och skicka diplom/enkät för "${instance.course?.name || instance.courseName}"?`)) return;
            try {
              const res = await instancesApi.lockAttendance(instance.id);
              setSuccess(res.data.message);
              fetchData();
            } catch (error) {
              setError(getErrorMessage(error));
            }
          }}
        />
      )}

      <CourseModal
        show={showCourseModal}
        editing={editingCourse}
        form={courseForm}
        setForm={setCourseForm}
        onSubmit={handleCourseSubmit}
        onClose={() => setShowCourseModal(false)}
      />

      <VenueModal
        show={showVenueModal}
        editing={editingVenue}
        form={venueForm}
        setForm={setVenueForm}
        onSubmit={handleVenueSubmit}
        onClose={() => setShowVenueModal(false)}
      />

      <InstanceModal
        show={showInstanceModal}
        editing={editingInstance}
        form={instanceForm}
        setForm={setInstanceForm}
        courses={courses}
        venues={venues}
        companies={companies}
        onSubmit={handleInstanceSubmit}
        onClose={() => { setShowInstanceModal(false); setError(''); }}
        error={showInstanceModal ? error : ''}
        onCreateCompany={() => {
          setCompanyForm({ ...EMPTY_COMPANY_FORM });
          setEditingCompany(null);
          setShowCompanyModal(true);
        }}
        onCreateVenue={() => {
          setVenueForm({ ...EMPTY_VENUE_FORM });
          setEditingVenue(null);
          setShowVenueModal(true);
        }}
      />

      <BookingModal
        show={showBookingForm}
        instance={selectedInstance}
        form={bookingData}
        setForm={setBookingData}
        success={bookingSuccess}
        onSubmit={handleBookingSubmit}
        onClose={() => setShowBookingForm(false)}
        formatDate={formatDate}
        formatPrice={formatPrice}
      />

      <SurveyResultsModal
        show={showSurveyModal}
        instance={surveyInstance}
        onClose={() => setShowSurveyModal(false)}
      />

      <CompanyModal
        show={showCompanyModal}
        editing={editingCompany}
        form={companyForm}
        setForm={setCompanyForm}
        onSubmit={handleCompanySubmit}
        onClose={() => setShowCompanyModal(false)}
      />
    </div>
  );
}

export default Courses;
